import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { isDemoUser } from '../utils/demoUtils';

const Demo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  useEffect(() => {
    const checkCurrentAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          // User is already authenticated
          const userData = await response.json();
          setCurrentUser(userData);
          
          // If already logged in as demo user, skip confirmation and proceed directly
          if (isDemoUser(userData.email)) {
            performDemoLogin();
          } else {
            // Different user is logged in, show confirmation
            setConfirmDialogOpen(true);
          }
        } else {
          // User is not authenticated, proceed directly with demo login
          performDemoLogin();
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // On error, assume not authenticated and proceed with demo login
        performDemoLogin();
      } finally {
        setCheckingAuth(false);
      }
    };

    checkCurrentAuth();
  }, []);

  const performDemoLogin = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/demo-login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Demo login successful, redirect to dashboard
        // Check if there are business ID query parameters to pass along
        const searchParams = new URLSearchParams(location.search);
        const businessIds = searchParams.get('businesses');
        
        if (businessIds) {
          // Redirect to dashboard with business parameters
          navigate(`/?businesses=${encodeURIComponent(businessIds)}`, { replace: true });
        } else {
          // Regular demo redirect
          navigate('/', { replace: true });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Demo login failed');
      }
    } catch (error) {
      console.error('Demo login error:', error);
      setError('An error occurred during demo login');
    }
  };

  const handleConfirmDemo = () => {
    setConfirmDialogOpen(false);
    performDemoLogin();
  };

  const handleCancelDemo = () => {
    setConfirmDialogOpen(false);
    navigate('/', { replace: true });
  };

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
        }}
      >
        <Typography variant="h5" align="center" gutterBottom color="error">
          Demo Unavailable
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
          {error}
        </Typography>
        <Typography variant="body2" align="center" color="textSecondary">
          Please try again later or contact support.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 4 }} />
        <Typography variant="h5" align="center" gutterBottom>
          {checkingAuth ? 'Checking Authentication...' : 'Loading Demo'}
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary">
          {checkingAuth ? 'Please wait while we check your login status...' : 'Setting up your demo experience...'}
        </Typography>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDemo}
        aria-labelledby="demo-confirm-dialog-title"
      >
        <DialogTitle id="demo-confirm-dialog-title">
          Continue to Demo?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're currently logged in as <strong>{currentUser?.email}</strong>. 
            Proceeding with the demo will log you out of your current account and log you into the demo account. 
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDemo}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDemo} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
              }
            }}
          >
            Continue to Demo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Demo; 